"use client";

import { useState, useSyncExternalStore } from "react";
import { RECIPES } from "@/lib/cauldron";

type Site = "sn4pe" | "davidfadon";

const CAULDRON_KEY = "sn4pe:cauldron:v1";
const STONE_KEY = "sn4pe:stone:v1";
const STONE_COIN_GRANT = 1000;

const noopSubscribe = () => () => {};
const trueSnapshot = () => true;
const falseSnapshot = () => false;

export default function DevSiteSwitcher({ current }: { current: Site }) {
    const [open, setOpen] = useState(false);
    const [unlocked, setUnlocked] = useState(false);
    const [granted, setGranted] = useState(false);
    const mounted = useSyncExternalStore(noopSubscribe, trueSnapshot, falseSnapshot);
    if (!mounted) return null;

    const switchTo = (site: Site) => {
        const url = new URL(window.location.href);
        url.searchParams.set("__site", site);
        window.location.href = url.toString();
    };

    const reset = () => {
        const url = new URL(window.location.href);
        url.searchParams.set("__site", "reset");
        window.location.href = url.toString();
    };

    const unlockCauldron = () => {
        const now = Date.now();
        let prev: Record<string, unknown> = {};
        try {
            const raw = localStorage.getItem(CAULDRON_KEY);
            if (raw) prev = JSON.parse(raw) ?? {};
        } catch {
            // ignore
        }
        const discoveredAt: Record<string, number> = {};
        for (const r of RECIPES) discoveredAt[r.id] = now;
        const next = {
            ...prev,
            discovered: RECIPES.map((r) => r.id),
            discoveredAt,
            pinned: Array.isArray(prev.pinned) ? prev.pinned : [],
            names: prev.names && typeof prev.names === "object" ? prev.names : {},
            brewCounts:
                prev.brewCounts && typeof prev.brewCounts === "object" ? prev.brewCounts : {},
            totalBrews: typeof prev.totalBrews === "number" ? prev.totalBrews : 0,
            totalFizzles: typeof prev.totalFizzles === "number" ? prev.totalFizzles : 0,
        };
        try {
            localStorage.setItem(CAULDRON_KEY, JSON.stringify(next));
        } catch {
            return;
        }
        setUnlocked(true);
        if (window.location.pathname === "/labs/cauldron") {
            window.location.reload();
        } else {
            window.setTimeout(() => setUnlocked(false), 1500);
        }
    };

    const grantStoneCoins = () => {
        let prev: Record<string, unknown> = {};
        try {
            const raw = localStorage.getItem(STONE_KEY);
            if (raw) prev = JSON.parse(raw) ?? {};
        } catch {
            // ignore
        }
        const currentSpent = typeof prev.spent === "number" ? prev.spent : 0;
        const next = { ...prev, spent: currentSpent - STONE_COIN_GRANT };
        try {
            localStorage.setItem(STONE_KEY, JSON.stringify(next));
        } catch {
            return;
        }
        setGranted(true);
        if (window.location.pathname === "/labs/stone") {
            window.location.reload();
        } else {
            window.setTimeout(() => setGranted(false), 1500);
        }
    };

    return (
        <div
            className="fixed z-50 font-mono text-[10px]"
            style={{
                bottom: "calc(env(safe-area-inset-bottom) + 0.75rem)",
                right: "calc(env(safe-area-inset-right) + 0.75rem)",
            }}
        >
            <div className="flex items-center gap-1 rounded-full border border-border bg-background/90 px-2 py-1 shadow-sm backdrop-blur">
                <span className="px-1 uppercase tracking-wide text-muted-foreground">dev</span>
                <button
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    className="rounded-full border border-border bg-foreground px-2 py-0.5 uppercase tracking-wide text-background"
                    aria-label={`${current} · cambiar sitio`}
                >
                    {current}
                </button>
                {open && (
                    <>
                        <button
                            type="button"
                            onClick={() => switchTo(current === "sn4pe" ? "davidfadon" : "sn4pe")}
                            className="rounded-full border border-border px-2 py-0.5 uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground"
                        >
                            → {current === "sn4pe" ? "davidfadon" : "sn4pe"}
                        </button>
                        <button
                            type="button"
                            onClick={reset}
                            className="rounded-full border border-border px-2 py-0.5 uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground"
                            title="Limpia override (vuelve al default)"
                        >
                            reset
                        </button>
                        <button
                            type="button"
                            onClick={unlockCauldron}
                            className="rounded-full border border-violet-500/60 px-2 py-0.5 uppercase tracking-wide text-violet-500 transition-colors hover:bg-violet-500/10"
                            title="Desbloquea todas las recetas del caldero"
                        >
                            {unlocked ? "✓ pócimas" : "♛ pócimas"}
                        </button>
                        <button
                            type="button"
                            onClick={grantStoneCoins}
                            className="rounded-full border border-amber-500/60 px-2 py-0.5 uppercase tracking-wide text-amber-500 transition-colors hover:bg-amber-500/10"
                            title={`Suma ${STONE_COIN_GRANT} monedas a tu piedra`}
                        >
                            {granted ? `✓ +${STONE_COIN_GRANT}💰` : `+${STONE_COIN_GRANT}💰 piedra`}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
